import { geoVerifyGeoserverStatusApi } from '@/api/geoserver/geoVerifyGeoserverStatusApiGwFuncs'
import { GEO_SERVER_STATUS } from '@/constants/api/geoserver/geoserverConstants'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { ERROR_MESSAGES } from '@/constants/error/errorMessage'
import type { GeoserverStatus } from '@/types/api/geoserver/geoserverTypes'
import { GlobalError } from '@/utils/globalError/globalError'
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

export const useGeoserverStatusStore = defineStore('geoserverStatusStore', () => {
  /** インターバルID */
  let intervalId: NodeJS.Timeout | null = null
  /** ポーリング間隔（msec） */
  const INTERVAL = 60 * 1000
  // API
  const { callVerifyGeoserverStatus } = geoVerifyGeoserverStatusApi()

  // state
  /** Geoserverステータス */
  const geoserverStatus = ref<GeoserverStatus | null>(null)

  /**
   * Geoserverステータスを取得し、stateを更新する
   * @param govCode 自治体ID
   * @param disasterId 災害ID
   * @returns なし
   */
  const fetchStatus = async (
    govCode: string | undefined,
    disasterId: string | undefined
  ): Promise<void> => {
    try {
      if (govCode === undefined || disasterId === undefined)
        throw Error(ERROR_MESSAGES[ERROR_CODES.GEO_SERVER_VERIFY_FAILED].message)
      const res = await callVerifyGeoserverStatus({ govCode, disasterId })
      geoserverStatus.value = res.result.status
    } catch (err) {
      console.error(err)
      const globalError = new GlobalError([ERROR_CODES.GEO_SERVER_VERIFY_FAILED])
      globalError.showErrorMessage()
    }
  }

  /**
   * ステータス確認開始
   */
  const startVerifyGeoserverStatus = async (
    govCode: string | undefined,
    disasterId: string | undefined,
    options: {
      initializeStatus?: GeoserverStatus
      startDelayMilliSecond?: number
    } = {}
  ): Promise<void> => {
    // すでにタイマーがあればクリア
    if (intervalId !== null) {
      clearInterval(intervalId)
    }
    if (options.initializeStatus != null) {
      geoserverStatus.value = options.initializeStatus
    }
    if (options.startDelayMilliSecond == null) {
      // 未指定の場合は即時実行
      options.startDelayMilliSecond = 0
    }

    // ディレイ時間後にステータスチェックのポーリング処理を開始
    setTimeout(async () => {
      await fetchStatus(govCode, disasterId)

      // 1分おきに実行
      intervalId = setInterval(async () => {
        await fetchStatus(govCode, disasterId)
      }, INTERVAL)
    }, options.startDelayMilliSecond)
  }

  /**
   * ステータス確認停止
   */
  const stopVerifyGeoserverStatus = (): void => {
    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
  }

  /**
   * storeの状態をリセットする
   */
  const reset = () => {
    stopVerifyGeoserverStatus()
    geoserverStatus.value = null
  }

  /**
   * Geoserverステータスの変化を検知して、ステータスに応じた処理を実行する
   */
  watch(geoserverStatus, (newValue, oldValue) => {
    if (newValue === GEO_SERVER_STATUS.RUNNING) {
      stopVerifyGeoserverStatus()
    } else if (oldValue && newValue === GEO_SERVER_STATUS.FAILED) {
      stopVerifyGeoserverStatus()

      // ジオサーバ起動失敗のエラーメッセージ表示
      const globalError = new GlobalError([ERROR_CODES.GEO_SERVER_RUN_FAILED])
      globalError.showErrorMessage()
    }
  })

  return {
    startVerifyGeoserverStatus,
    stopVerifyGeoserverStatus,
    reset,
    geoserverStatus
  }
})
